import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getUserQuotes, updateUser, getMyFollows, getFollowFeed, toggleNotifications } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cancelSubscription } from '@/lib/api';
import NotificationSettings from '@/components/NotificationSettings';
import MyLeadsSection from '@/components/MyLeadsSection';
import { logger } from '@/lib/logger';
import { 
  User, FileText, Search, CreditCard, 
  Clock, ArrowRight, Mail, Settings, MessageSquare, XCircle, CalendarDays, Heart, Building2, Bell, Sparkles, MapPin, Pencil, Save, X
} from 'lucide-react';

const statusColors = {
  pending: 'bg-[#f5e6d8] text-[#6b3e25]',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800'
};

const statusLabels = {
  pending: 'Afventer',
  accepted: 'Accepteret',
  rejected: 'Afvist',
  completed: 'Afsluttet'
};

export default function UserDashboardPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingNewsletter, setUpdatingNewsletter] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [follows, setFollows] = useState([]);
  const [feed, setFeed] = useState([]);
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ address: '', postal_code: '' });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (user?.user_type === 'company') {
      navigate('/company-dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [quotesRes, followsRes, feedRes] = await Promise.all([
          getUserQuotes(),
          getMyFollows(),
          getFollowFeed()
        ]);
        setQuotes(quotesRes.data);
        setFollows(followsRes.data);
        setFeed(feedRes.data);
      } catch (error) {
        logger.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const handleNewsletterToggle = async (field, checked) => {
    setUpdatingNewsletter(true);
    try {
      await updateUser({ [field]: checked });
      await refreshUser();
      toast.success('Nyhedsbrevindstillinger opdateret');
    } catch (error) {
      toast.error('Kunne ikke opdatere indstillinger');
    } finally {
      setUpdatingNewsletter(false);
    }
  };

  const handleStartEditAddress = () => {
    setAddressForm({
      address: user?.address || '',
      postal_code: user?.postal_code || ''
    });
    setEditingAddress(true);
  };

  const handleSaveAddress = async () => {
    if (!/^\d{4}$/.test(addressForm.postal_code.trim())) {
      toast.error('Indtast et gyldigt 4-cifret postnummer');
      return;
    }
    setSavingAddress(true);
    try {
      await updateUser({
        address: addressForm.address.trim(),
        postal_code: addressForm.postal_code.trim()
      });
      await refreshUser();
      setEditingAddress(false);
      toast.success('Adresse opdateret');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opdatere adresse');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Er du sikker på, at du vil opsige dit abonnement? Du beholder adgang resten af den betalte periode.')) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      await refreshUser();
      toast.success('Abonnement opsagt. Du har adgang til periodens udgang.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opsige abonnement');
    } finally {
      setCancelling(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-stone-50 py-8">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <Skeleton className="h-32 w-full mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="user-dashboard">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Velkommen, {user.name}!</h1>
              <p className="text-white/80">Dit personlige dashboard</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/find-automatisk')} data-testid="find-auto-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bronze-shine text-white">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Find automatisk</h3>
                <p className="text-sm text-muted-foreground">Vi matcher dig med op til 3</p>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/directory')} data-testid="find-tradesperson-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Find Håndværker</h3>
                <p className="text-sm text-muted-foreground">Søg og filtrer</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-primary to-primary/80 text-white">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1">Tilbudsanmodninger</h3>
              <p className="text-3xl font-bold">{quotes.length}</p>
              <p className="text-sm text-white/80">Totalt sendt</p>
            </CardContent>
          </Card>
        </div>

        {/* Auto-match leads */}
        <div className="mb-8">
          <MyLeadsSection />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quote Requests */}
          <div className="lg:col-span-2">
            <Card data-testid="quotes-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Dine tilbudsanmodninger
                </CardTitle>
                <CardDescription>
                  Se status på dine sendte anmodninger
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : quotes.length > 0 ? (
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <div 
                        key={quote.id} 
                        className="flex items-start justify-between p-4 rounded-lg border border-border/50 hover:border-primary/20 transition-colors"
                        data-testid={`quote-${quote.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Link 
                              to={`/company/${quote.company_id}`}
                              className="font-semibold hover:text-primary transition-colors"
                            >
                              {quote.company_name}
                            </Link>
                            <Badge className={statusColors[quote.status]}>
                              {statusLabels[quote.status]}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {quote.description}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(quote.created_at).toLocaleDateString('da-DK')}
                            </span>
                            {quote.preferred_date && (
                              <span>Ønsket dato: {quote.preferred_date}</span>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/company/${quote.company_id}`}>
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Ingen tilbudsanmodninger endnu</h3>
                    <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                      Start med at finde en håndværker og send din første tilbudsanmodning
                    </p>
                    <Button onClick={() => navigate('/directory')} data-testid="find-tradesperson-button">
                      Find håndværker
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Following & Feed */}
          <div className="lg:col-span-2 space-y-6">
            {/* Feed from followed companies */}
            {feed.length > 0 && (
              <Card data-testid="feed-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Nyheder fra virksomheder du følger
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {feed.slice(0, 6).map((post, idx) => (
                    <div 
                      key={post.id} 
                      className="bg-white rounded-2xl border border-border/60 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`feed-post-${post.id}`}
                    >
                      {/* Post header */}
                      <div className="flex items-center gap-3 p-4 pb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#112110] to-[#c97f59] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {(post.company_name || 'V').charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link to={`/company/${post.company_id}`} className="font-semibold text-sm truncate hover:underline">{post.company_name}</Link>
                          <p className="text-xs text-muted-foreground">
                            {new Date(post.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      </div>

                      {/* Post image */}
                      <img src={post.image} alt={post.caption || 'Opslag'} className="w-full max-h-[400px] object-cover" />

                      {/* Caption */}
                      {post.caption && (
                        <div className="px-4 py-3">
                          <p className="text-sm leading-relaxed">
                            <span className="font-semibold mr-1.5">{post.company_name}</span>
                            {post.caption}
                          </p>
                        </div>
                      )}

                      {/* Accent line */}
                      <div className="h-0.5 bg-gradient-to-r from-[#112110] via-[#c97f59] to-transparent" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Companies you follow */}
            <Card data-testid="follows-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Virksomheder du følger ({follows.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {follows.length > 0 ? (
                  <div className="space-y-3">
                    {follows.map((follow) => (
                      <Link
                        key={follow.id}
                        to={`/company/${follow.company_id}`}
                        className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/20 transition-colors"
                        data-testid={`follow-${follow.company_id}`}
                      >
                        <span className="font-medium">{follow.company_name}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground text-sm">Du følger ingen virksomheder endnu</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/directory')}>
                      Find virksomheder
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Settings Sidebar */}
          <div className="space-y-6">

            <Card data-testid="settings-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Indstillinger
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Newsletter Email Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <Label htmlFor="newsletter-email-toggle" className="font-medium">
                        Nyhedsbrev (e-mail)
                      </Label>
                    </div>
                  </div>
                  <Switch
                    id="newsletter-email-toggle"
                    checked={user.newsletter_email || false}
                    onCheckedChange={(checked) => handleNewsletterToggle('newsletter_email', checked)}
                    disabled={updatingNewsletter}
                    data-testid="newsletter-email-toggle"
                  />
                </div>

                {/* Newsletter SMS Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-muted-foreground" />
                      <Label htmlFor="newsletter-sms-toggle" className="font-medium">
                        Nyhedsbrev (SMS)
                      </Label>
                    </div>
                  </div>
                  <Switch
                    id="newsletter-sms-toggle"
                    checked={user.newsletter_sms || false}
                    onCheckedChange={(checked) => handleNewsletterToggle('newsletter_sms', checked)}
                    disabled={updatingNewsletter}
                    data-testid="newsletter-sms-toggle"
                  />
                </div>

                {/* Account Info */}
                <div className="pt-4 border-t space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Navn</p>
                    <p className="font-medium">{user.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                  {user.phone && (
                    <div>
                      <p className="text-xs text-muted-foreground">Telefon</p>
                      <p className="font-medium">{user.phone}</p>
                    </div>
                  )}

                  {/* Address (editable) */}
                  <div className="pt-3 border-t" data-testid="address-section">
                    {editingAddress ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> Adresse
                          </p>
                          <button
                            type="button"
                            onClick={() => setEditingAddress(false)}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid="address-cancel-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <Input
                          type="text"
                          placeholder="Vejnavn og husnummer"
                          value={addressForm.address}
                          onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                          data-testid="address-input"
                        />
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Postnummer (fx 2100)"
                          value={addressForm.postal_code}
                          onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          maxLength={4}
                          data-testid="postal-input"
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveAddress}
                          disabled={savingAddress}
                          className="w-full"
                          data-testid="address-save-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {savingAddress ? 'Gemmer…' : 'Gem adresse'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> Adresse
                          </p>
                          {(user.address || user.postal_code) ? (
                            <p className="font-medium text-sm break-words" data-testid="address-display">
                              {user.address}
                              {user.postal_code && !(user.address || '').includes(user.postal_code) && (
                                <>{user.address ? ', ' : ''}{user.postal_code}</>
                              )}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">Ikke angivet</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleStartEditAddress}
                          className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1 shrink-0"
                          data-testid="address-edit-btn"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          {(user.address || user.postal_code) ? 'Redigér' : 'Tilføj'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <NotificationSettings userType="user" />
          </div>
        </div>
      </div>
    </div>
  );
}
